import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { BaseApiService, ApiResponse } from "./baseApi";

// MIS Report DTO - represents data for one month with all metrics
export interface MISReportMonthDto {
  monthKey: string;
  year: string;
  month: string;
  monthEndDate: string;
  branchId: number;
  branchName: string;
  branchCode: string;
  rmStockQtyAsOnDate: number;
  rmStockValueAsOnDate: number;
  rmConsumptionQty: number;
  rmConsumptionValue: number;
  blankingPartsProduced: number;
  blankingPartsRejected: number;
  autoRetainerPartsProduced: number;
  autoRetainerPartsRejected: number;
  weldingTappingPartsProduced: number;
  weldingTappingPartsRejected: number;
  retainerPartsProduced: number;
  retainerPartsRejected: number;
}

// Transformed MIS Report DTO - represents data for one metric across months
export interface MISReportDto {
  metric: string;
  months: { [monthKey: string]: number | string };
}

// MIS Daily Report DTO
export interface MISDailyReportDto {
  dayKey?: string;
  year?: string;
  month?: string;
  day?: string;
  reportDate?: string;
  branchId?: number;
  branchName?: string;
  branchCode?: string;
  rmStockQtyAsOnDate?: number;
  rmStockValueAsOnDate?: number;
  rmConsumptionQty?: number;
  rmConsumptionValue?: number;
  blankingPartsProduced?: number;
  blankingPartsRejected?: number;
  autoRetainerPartsProduced?: number;
  autoRetainerPartsRejected?: number;
  weldingTappingPartsProduced?: number;
  weldingTappingPartsRejected?: number;
  retainerPartsProduced?: number;
  retainerPartsRejected?: number;
}

// MIS Report API Service
class MISReportApiService extends BaseApiService {
  
  // Get MIS report by branch (monthly - kept for backward compatibility)
  async getMISReportByBranch(branchId: number): Promise<ApiResponse<MISReportDto[]>> {
    try {
      const data = await this.makeRequest<MISReportMonthDto[]>(
        API_ENDPOINTS.MIS_REPORTS.BY_BRANCH(branchId),
        { method: HTTP_METHODS.GET }
      );
      
      // Transform from month-based to metric-based structure
      const transformedData = this.transformMISData(data);
      
      return {
        success: true,
        message: 'MIS report fetched successfully',
        data: transformedData
      };
    } catch (error) {
      // console.error('MISReportApiService: Error fetching MIS report:', error);
      toast.error('Failed to fetch MIS report');
      throw error;
    }
  }

  // Get MIS daily summary report by branch and date range (GET method)
  async getMISDailySummaryByBranchAndDateRange(
    branchId: number,
    startDate: string,
    endDate: string
  ): Promise<ApiResponse<MISDailyReportDto[]>> {
    try {
      const data = await this.makeRequest<MISDailyReportDto[]>(
        API_ENDPOINTS.MIS_REPORTS.DAILY_BY_BRANCH_AND_DATE_RANGE(branchId, startDate, endDate),
        { method: HTTP_METHODS.GET }
      );
      
      return {
        success: true,
        message: 'MIS daily summary report fetched successfully',
        data: Array.isArray(data) ? data : []
      };
    } catch (error) {
      // console.error('MISReportApiService: Error fetching MIS daily summary:', error);
      toast.error('Failed to fetch MIS daily summary report');
      throw error;
    }
  }

  // Transform month-based data to metric-based data
  private transformMISData(data: MISReportMonthDto[]): MISReportDto[] {
    // Metric mapping: metric name -> API field name
    const metricMapping = [
      { metric: 'RM Stock As on dated (end of month)', field: 'rmStockQtyAsOnDate' },
      { metric: 'Consumption of RM (monthly)', field: 'rmConsumptionQty' },
      { metric: 'No. of Final parts produced in Blanking Section', field: 'blankingPartsProduced' },
      { metric: 'No. of parts rejected in Blanking Section', field: 'blankingPartsRejected' },
      { metric: 'No. of Retainer produced in Auto Section', field: 'autoRetainerPartsProduced' },
      { metric: 'No. of Retainer rejected in Auto Section', field: 'autoRetainerPartsRejected' },
      { metric: 'No. of Parts produced In Welding + Tapping section', field: 'weldingTappingPartsProduced' },
      { metric: 'No. of parts rejected in Welding And Tapping', field: 'weldingTappingPartsRejected' },
      { metric: 'No. of parts Produced in a retainer Section (TRB+Forming+Facing)', field: 'retainerPartsProduced' },
      { metric: 'No. of parts rejected at Retainer Line', field: 'retainerPartsRejected' },
    ];

    // Initialize result structure
    const result: MISReportDto[] = metricMapping.map(({ metric }) => ({
      metric,
      months: {}
    }));

    // Populate months data
    data.forEach((monthData) => {
      const monthKey = monthData.monthKey;
      
      metricMapping.forEach(({ metric, field }, index) => {
        const value = (monthData as any)[field];
        result[index].months[monthKey] = value !== null && value !== undefined ? value : 0;
      });
    });

    return result;
  }
}

export const misReportApiService = new MISReportApiService();

